import React from "react";
import EditorSearchCommentItem from "./EditorSearchCommentItem";
import EditorSearchContextPanel from "./EditorSearchContextPanel";
import PropTypes from "prop-types";
import useBackboneModel from "./helper/useBackboneModel";
import SequencerSearchWidget from "models/audioEditor/SequencerSearchWidget";
import { useEffect, useState, useRef } from 'react';
import Utils from "../models/helper/Utils";
import { Virtuoso } from 'react-virtuoso';
import { useWindowSize } from "@uidotdev/usehooks";
import ti18n from 'i18n!nls/Sequenceri18n';
import Empty from "./common/Empty";
import TrebbleSkeleton from "./common/Skeleton";

// `descriptionParts` is rendered as text by EditorSearchCommentItem; `description` is the same
// excerpt as plain text for analytics.
const getSearchResultDescriptionProps = (searchResultArray) =>
{
    const descriptionParts = Utils.getInstance().getSequencerSearchResultItemDescriptionParts(searchResultArray);
    return {
        description: descriptionParts.before + descriptionParts.highlighted + descriptionParts.after,
        descriptionParts,
    };
};

const EditorSearchPanel = (props) =>
{

    const size = useWindowSize();
    const SEARCH_CONTEXT_WINDOW_HEIGHT = 132;
    const SEARCH_QUERY_PRESET_I18N_PREFIX = "AI_SEARCH_QUERY_PRESET_";
    const AI_SEARCH_RESULT_CONFIDENCE_LEVEL_PREFIX = "AI_SEARCH_RESULT_CONFIDENCE_LEVEL_";
    const AI_SEARCH_RESULT_RECOMMENDATION_TYPE_PREFIX = "AI_SEARCH_RESULT_RECOMMENDATION_";
    const AI_SEARCH_RESULT_STREAMLINED_CONFIDENCE_LEVEL_PREFIX = "AI_SEARCH_RESULT_STREAMLINED_RECOMMENDATION_";
    const getSearchQueryPresetTypeLabel = (searchPresetEnumValue)=>{
       return window.getI18n(ti18n, SEARCH_QUERY_PRESET_I18N_PREFIX+ searchPresetEnumValue);
    }

    const getSearchResultConfidenceLevelLabel = (confidenceLevelEnumValue)=>{
        return window.getI18n(ti18n, AI_SEARCH_RESULT_CONFIDENCE_LEVEL_PREFIX+ Utils.getInstance().getAISuggestionConfidenceLevelEnumKeyFromValue(confidenceLevelEnumValue));
    }

    const getSearchResultStreamlinedConfidenceLevelLabel = (confidenceLevelEnumValue)=>{
        return window.getI18n(ti18n, AI_SEARCH_RESULT_STREAMLINED_CONFIDENCE_LEVEL_PREFIX+ Utils.getInstance().getAISuggestionStreamlinedConfidenceLevelEnumKeyFromValue(confidenceLevelEnumValue));
    }

    const getSearchResultRecommendationTypeLabel = (recommendationTypeEnumValue)=>{
        return window.getI18n(ti18n, AI_SEARCH_RESULT_RECOMMENDATION_TYPE_PREFIX+ Utils.getInstance().getAISuggestionRecommendationTypesEnumKeyFromValue(recommendationTypeEnumValue));
    }

    const virtualListRef = useRef(null);
    const searchWidgetData = useBackboneModel(props.searchWidgetModel, (model) =>
    {
        return {
            searchRequestId: model.getSearchRequestId(),
            searchResultIndexInFocus: model.getIndexOfNodeInFocus(),
            numberOfSearchNodesFound: model.getNumberOfSearchNodesFound(),
            arrayOfSequencerNodeInFocus: model.getSequencerNodesCurrentlyInFocus(),
            canMoveToPreviousResult: model.canMoveToPreviousResult(),
            canMoveToNextResult: model.canMoveToNextResult(),
            allArraysOfNodesMatchingResults: model.getAllArraysOfNodesMatchingResults(),
            allArraysOfNodesToHiglights: model.getAllArraysOfNodesToHiglights(),
            allArraysOfAdditionalMatchingResults: model.getAllArraysOfAdditionalMatchingResults(),
            allSafeTrimBoundaries: model.get("allSafeTrimBoundaries"),
            searchParams: model.getSearchParams(),
            searchType: model.getSearchType(),
            searchOn: model.isSearchOn(),
            isMediaAnalyzedForSilences: model.isMediaAnalyzedForSilences && model.isMediaAnalyzedForSilences(),
            mediaAnalysisProgress: model.getMediaAnalysisProgress && model.getMediaAnalysisProgress(),
            shouldSafeBeAvailable: model.shouldSafeBeAvailableForCurrentSearchType && model.shouldSafeBeAvailableForCurrentSearchType()
        };
    });

    const [ hasResults, setHasResults ] = useState(searchWidgetData.allArraysOfNodesMatchingResults && searchWidgetData.allArraysOfNodesMatchingResults.length > 0);

    useEffect(() =>
    {
        setHasResults(searchWidgetData.allArraysOfNodesMatchingResults && searchWidgetData.allArraysOfNodesMatchingResults.length > 0);
    }, [ searchWidgetData ]);


    const [ searchResultsInfo, setSearchResultsInfo ] = useState([]);

    const [ visibleRange, setVisibleRange ] = useState({
        startIndex: 0,
        endIndex: 0,
    })

    useEffect(() =>
    {
        if (searchWidgetData.allArraysOfNodesMatchingResults && searchWidgetData.allArraysOfNodesMatchingResults.length > 0)
        {
            const additionResults = (searchWidgetData.allArraysOfAdditionalMatchingResults || []).filter(r => r && r.isAddition);
            console.log('[ELM-TRACE] EditorSearchPanel: total results:', searchWidgetData.allArraysOfNodesMatchingResults.length, ', additions:', additionResults.length, additionResults.map(r => ({ type: r.type, isAddition: r.isAddition, additionData: r.additionData ? { type: r.additionData.type, title: r.additionData.fileInfo?.title } : null })));
            const allSafeTrimBoundaries = searchWidgetData.allSafeTrimBoundaries || [];
            setSearchResultsInfo(searchWidgetData.allArraysOfNodesMatchingResults.map((searchResultArray, index) =>
            {
                return {
                    searchRequestId: searchWidgetData.searchRequestId,
                    ...getSearchResultDescriptionProps(searchResultArray),
                    uid: searchWidgetData.allArraysOfAdditionalMatchingResults.length > 0 ?searchWidgetData.allArraysOfAdditionalMatchingResults[index].uid: null,
                    reason: searchWidgetData.allArraysOfAdditionalMatchingResults.length > 0 ? searchWidgetData.allArraysOfAdditionalMatchingResults[index].reason : null,
                    type: searchWidgetData.allArraysOfAdditionalMatchingResults.length > 0 ? searchWidgetData.allArraysOfAdditionalMatchingResults[index].type : null,
                    typeLabel: searchWidgetData.allArraysOfAdditionalMatchingResults.length > 0 ? getSearchQueryPresetTypeLabel(searchWidgetData.allArraysOfAdditionalMatchingResults[index].type) || searchWidgetData.allArraysOfAdditionalMatchingResults[index].customTypeLabel : null,
                    positionLabel: searchWidgetData.allArraysOfAdditionalMatchingResults.length > 0 ? searchWidgetData.allArraysOfAdditionalMatchingResults[index].positionLabel : null,
                    isCustomType: searchWidgetData.allArraysOfAdditionalMatchingResults.length > 0 ? searchWidgetData.allArraysOfAdditionalMatchingResults[index].isCustomType : null,
                    customTypeLabel: searchWidgetData.allArraysOfAdditionalMatchingResults.length > 0 ? searchWidgetData.allArraysOfAdditionalMatchingResults[index].customTypeLabel : null,
                    confidence: searchWidgetData.allArraysOfAdditionalMatchingResults.length > 0 ? searchWidgetData.allArraysOfAdditionalMatchingResults[index].confidence : null,
                    confidenceLabel: searchWidgetData.allArraysOfAdditionalMatchingResults.length > 0 ? getSearchResultConfidenceLevelLabel(searchWidgetData.allArraysOfAdditionalMatchingResults[index].confidence) : null,
                    streamlinedSuggestionConfidence: searchWidgetData.allArraysOfAdditionalMatchingResults.length > 0 ? searchWidgetData.allArraysOfAdditionalMatchingResults[index].streamlinedSuggestionConfidence : null,
                    streamlinedSuggestionConfidenceLabel: searchWidgetData.allArraysOfAdditionalMatchingResults.length > 0 ? getSearchResultStreamlinedConfidenceLevelLabel(searchWidgetData.allArraysOfAdditionalMatchingResults[index].streamlinedSuggestionConfidence) : null,
                    recommendation: searchWidgetData.allArraysOfAdditionalMatchingResults.length > 0 ? searchWidgetData.allArraysOfAdditionalMatchingResults[index].recommendation : null,
                    recommendationLabel: searchWidgetData.allArraysOfAdditionalMatchingResults.length > 0 ? getSearchResultRecommendationTypeLabel(searchWidgetData.allArraysOfAdditionalMatchingResults[index].recommendation) : null,
                    isAddition: searchWidgetData.allArraysOfAdditionalMatchingResults.length > 0 ? searchWidgetData.allArraysOfAdditionalMatchingResults[index].isAddition || false : false,
                    additionData: searchWidgetData.allArraysOfAdditionalMatchingResults.length > 0 ? searchWidgetData.allArraysOfAdditionalMatchingResults[index].additionData || null : null,
                    // Per-result safe-trim boundary (carries ms-precise silence
                    // info + safeToCut flag). Downstream UI can read this to
                    // show warnings for unsafe cuts; the apply handler already
                    // uses the whole-node expansion baked into searchResultArray.
                    safeTrimBoundary: allSafeTrimBoundaries[index] || null,
                    searchResultArray, sequencer: props.sequencer, index: index };
            }));
        } else
        {
            setSearchResultsInfo([]);
        }
    }, [ searchWidgetData]);

    const prevFocusIndex = useRef(searchWidgetData.searchResultIndexInFocus);
    
    useEffect(() =>
    {
        // Only auto-scroll when the focus index actually changes (programmatically),
        // not when the user scrolls manually
        if (
            virtualListRef.current &&
            prevFocusIndex.current !== searchWidgetData.searchResultIndexInFocus &&
            (searchWidgetData.searchResultIndexInFocus < visibleRange.startIndex || searchWidgetData.searchResultIndexInFocus > visibleRange.endIndex)
        )
        {
            virtualListRef.current.scrollToIndex({
                index: searchWidgetData.searchResultIndexInFocus
            });
        }
        
        prevFocusIndex.current = searchWidgetData.searchResultIndexInFocus;
    }, [ searchWidgetData.searchResultIndexInFocus, visibleRange ]);


    const [ panelOpened, setPanelOpened ] = useState(false);

    useEffect(() =>
    {
        if (props.onPanelOpen)
        {
            props.onPanelOpen();
            setPanelOpened(true);
        }
    }, []);


    return (
        <div className="editor-search-browse-panel flex flex-col p-2 h-full w-full max-w-sm relative" style={ { width: "310px" } }>


            <EditorSearchContextPanel { ...props } panelOpened={ panelOpened } className="flex-initial fixed" />
            <div className="flex flex-col overflow-auto flex-auto flex-auto add_gradient_at_bottom">
                

                {/* Show loading spinner if media is being analyzed for silences */}
                {searchWidgetData.isMediaAnalyzedForSilences ? (
                    <div className="flex flex-col items-center justify-center h-full w-full">
                        <TrebbleSkeleton className="py-5 w-full" paragraph={{ rows: 6, width: "100%" }} active useEditorTheme={true} />
                    </div>
                ) : (
                    <>
                        { !hasResults && <div className="flex flex-col items-center justify-center h-full">{searchWidgetData.searchParams && <Empty useEditorTheme={true} description={window.getI18n(ti18n, "NO_RESULT_FOUND")}/>}</div> }
                <Virtuoso
                            ref={ virtualListRef }
                            rangeChanged={ setVisibleRange }
                            className={ !hasResults ? "hidden" : "" }
                            style={ { height: props.listHeightOffset ? size.height - props.listHeightOffset - SEARCH_CONTEXT_WINDOW_HEIGHT : size.height } }
                            totalCount={ searchResultsInfo.length }
                            itemContent={ (index) =>
                            {
                                const aResultInfo = searchResultsInfo[ index ];
                                // Addition suggestions: override description and action
                                if (aResultInfo.isAddition && aResultInfo.additionData) {
                                    const ad = aResultInfo.additionData;
                                    const fileTitle = (ad.fileInfo && ad.fileInfo.title) || window.getI18n(ti18n, 'AUDIO_FILE') || 'Audio file';
                                    // Prefer first typeOption label when top-level type is generic 'sound_effect'
                                    const firstTypeOptionLabel = ad.typeOptions && ad.typeOptions.length > 0 ? ad.typeOptions[0].label : null;
                                    const additionType = (ad.type || '').toLowerCase();
                                    const additionTypeLabels = {
                                        intro_music: window.getI18n(ti18n, 'AI_SEARCH_QUERY_PRESET_INTRO_MUSIC'),
                                        outro_music: window.getI18n(ti18n, 'AI_SEARCH_QUERY_PRESET_OUTRO_MUSIC'),
                                        background_music: window.getI18n(ti18n, 'AI_SEARCH_QUERY_PRESET_BACKGROUND_MUSIC'),
                                        sound_effect: window.getI18n(ti18n, 'AI_SEARCH_QUERY_PRESET_SOUND_EFFECT')
                                    };
                                    const typeLabel = (ad.category === 'wrap' || additionType === 'background_music') ?
                                        additionTypeLabels.background_music :
                                        firstTypeOptionLabel || additionTypeLabels[additionType] || aResultInfo.customTypeLabel || ad.typeLabel || getSearchQueryPresetTypeLabel(ad.type) || ad.type || '';
                                    const posLabel = ad.positionLabel || aResultInfo.positionLabel || getSearchQueryPresetTypeLabel(ad.positionType) || ad.positionType || '';
                                    const additionDescriptionParts = { before: fileTitle + ' ', detail: '\u2014 ' + typeLabel + ' \u00B7 ' + posLabel };
                                    return <div className="px-2 py-1" key={ index }><EditorSearchCommentItem
                                        searchType="additionSuggestion"
                                        selected={ searchWidgetData.searchResultIndexInFocus === index }
                                        { ...aResultInfo }
                                        description={ additionDescriptionParts.before + additionDescriptionParts.detail }
                                        descriptionParts={ additionDescriptionParts }
                                        typeLabel={ typeLabel }
                                        onApplyButtonClick={ () => {
                                            const additionApplied = props.sequencer.applyAdditionSuggestion(ad, aResultInfo.searchResultArray[0]);
                                            if(additionApplied !== false){
                                                props.searchWidgetModel.recordAppliedSuggestionFeedbackForAdditionalMatchingResult(aResultInfo, aResultInfo.searchResultArray);
                                                props.searchWidgetModel.dismissSpecificSearchResult(index, true);
                                            }
                                        } }
                                        onClick={ () => { props.searchWidgetModel.moveToNextFoundNode(true, index, true) } }
                                    /></div>;
                                }
                                return <div className="px-2 py-1" key={ index }><EditorSearchCommentItem searchType={searchWidgetData.searchType} selected={ searchWidgetData.searchResultIndexInFocus === index } { ...aResultInfo } onApplyButtonClick={ () => {
                                    props.searchWidgetModel.deleteSelectedNodesWithAutoJumpLogic(aResultInfo.searchResultArray[ 0 ], aResultInfo.searchResultArray[ aResultInfo.searchResultArray.length - 1 ], props.searchWidgetModel.createDeletionSourceFromResult(null, false, aResultInfo), aResultInfo)
                                }
                                } onClick={ () => { props.searchWidgetModel.moveToNextFoundNode(true, index, true) } } /></div>
                            } }
                        />
                    </>
                )}
            </div>
        </div>
    )
}

EditorSearchPanel.propTypes = {
    searchDisplayTitle: PropTypes.string.isRequired,
    resultCursor: PropTypes.number,
    totalNumberOfResults: PropTypes.number,
    applyAllActionButtonLabel: PropTypes.string,
    listHeight: PropTypes.number,
    listHeightOffset: PropTypes.number,
    searchWidgetModel: PropTypes.object,
    sequencer: PropTypes.object,
    comments: PropTypes.array,
    onPanelOpen: PropTypes.func,
    onPanelCloseButtonClick: PropTypes.func,
}


export default EditorSearchPanel;
